import { calculateBankDiscreationaryIncome, calculateHouseholdAccounts, calculatePrivateLoanExpenses } from "../household";
import { ForecastDefault } from "../../../forecast";
import { calculateHousingExpenses } from "../housing";
import { Taxation } from "../household/taxation/taxation";
import { deepClone, sum } from "../../../utils";
import { calculateSavings } from "../../../savings";
import { calculateForecastResultAfterInterestDeduction } from "../interest";
const MONTHS_IN_YEAR = 12;
const defaultForecastLocalizationParameters = {
    mortgage: {
        amortizationDebtToIncomeFactor: 4.5,
        highLoanToValueThreshold: 0.7,
        lowLoanToValueThreshold: 0.5,
        highAmortizationRate: 0.02,
        lowAmortizationRate: 0.01,
        incomeBasedAmortizationRate: 0.01,
        interestDeductionThreshold: 100000,
        baseInterestDeductionRate: 0.3,
        extraInterestDeductionRate: 0.21,
    },
    household: {
        estimatedSalaryIncreaseFactor: 1.018,
        retirementAge: 66,
        costOfAdults: [11400, 18500],
        costOfChild: {
            fullTime: 4100,
            halfTime: 2050,
        },
        maxChildAge: 20,
    },
};
export class Forecast extends ForecastDefault {
    constructor(localizationParameters) {
        super(localizationParameters !== null && localizationParameters !== void 0 ? localizationParameters : defaultForecastLocalizationParameters);
    }
    /**
     * Forecast user personal finance and custom bank discreationary income from today using swedish market regulations and assumptions
     *
     * @param input Forecast input for swedish market
     * @param nrOfMonths Forecast length in months, evenly divisible by 12
     * @param transformInput A function to run on the input data for each month to transform it
     * for the next month. Can be used to implement scenarios and similar cases.
     * @returns Full forecast and custom bank discreationary income for first month
     */
    forecastAll(input, nrOfMonths, transformInput) {
        var _a, _b, _c;
        const inputClone = deepClone(input);
        const resultsBeforeInterestDeduction = [];
        let mortgageAmount = sum(inputClone.mortgage, (loan) => loan.amount);
        const amortizationHistory = {
            loanToValueAmortizationRate: 1,
            debtToIncomeAmortizationRate: 1,
            mortgageAmountAtRequirement: mortgageAmount,
            highestHouseholdSalaryIncome: 0,
        };
        const taxation = new Taxation(this.localizationParameters.household.retirementAge);
        for (let month = 0; month < nrOfMonths; ++month) {
            if (transformInput) {
                transformInput(inputClone, month);
            }
            const currentHouseholdSalaryIncome = sum(inputClone.adults, (adult) => adult.income);
            amortizationHistory.highestHouseholdSalaryIncome = Math.max(currentHouseholdSalaryIncome, amortizationHistory.highestHouseholdSalaryIncome);
            mortgageAmount = sum(inputClone.mortgage, (loan) => loan.amount);
            const additionalMortgageAmount = (_b = (_a = inputClone.additionalHousing) === null || _a === void 0 ? void 0 : _a.mortgage) === null || _b === void 0 ? void 0 : _b.reduce((prev, next) => prev + next.amount, 0);
            const housingExpenses = calculateHousingExpenses(inputClone, amortizationHistory, this.localizationParameters.mortgage);
            const privateLoans = calculatePrivateLoanExpenses(inputClone.privateLoans);
            const householdAccounts = calculateHouseholdAccounts(inputClone, housingExpenses, privateLoans, taxation, this.localizationParameters.household);
            const savings = calculateSavings(inputClone.savings);
            const monthlyResult = {
                mortgageAmount,
                totalMonthlyAmortization: housingExpenses.totalMonthlyAmortization,
                totalMonthlyInterestBeforeDeduction: housingExpenses.totalMonthlyInterestBeforeDeduction,
                totalMonthlyInterestAfterDeduction: 0,
                amortizationRate: housingExpenses.loanToValueAmortizationRate + housingExpenses.debtToIncomeAmortizationRate,
                additionalMortgageAmount,
                additionalMortgageInterest: (_c = housingExpenses.additionalHousingExpenses.interestBeforeDeduction) !== null && _c !== void 0 ? _c : 0,
                maintenance: housingExpenses.maintenance,
                fee: housingExpenses.fee,
                propertyTax: housingExpenses.propertyTax,
                grossIncome: householdAccounts.grossIncome,
                netIncome: householdAccounts.netIncome,
                householdIncome: householdAccounts.householdIncome,
                householdExpenses: householdAccounts.householdExpenses,
                housingExpensesBeforeTaxDeduction: housingExpenses.housingExpensesBeforeTaxDeduction,
                housingExpensesAfterTaxDeduction: 0,
                discreationaryIncome: householdAccounts.discreationaryIncome,
                privateLoans,
                savings,
            };
            resultsBeforeInterestDeduction.push(monthlyResult);
            // Amortization related values to swedish amortization requirements
            if (housingExpenses.loanToValueAmortizationRate < amortizationHistory.loanToValueAmortizationRate) {
                amortizationHistory.mortgageAmountAtRequirement = mortgageAmount;
                if (amortizationHistory.loanToValueAmortizationRate <= this.localizationParameters.mortgage.highAmortizationRate) {
                    amortizationHistory.mortgageAmountAtRequirement =
                        housingExpenses.loanToValueAmortizationRate >= this.localizationParameters.mortgage.lowAmortizationRate
                            ? input.housingValue * this.localizationParameters.mortgage.highLoanToValueThreshold
                            : input.housingValue * this.localizationParameters.mortgage.lowLoanToValueThreshold;
                }
            }
            amortizationHistory.loanToValueAmortizationRate = housingExpenses.loanToValueAmortizationRate;
            amortizationHistory.debtToIncomeAmortizationRate = housingExpenses.debtToIncomeAmortizationRate;
            const { monthlyAmortization } = housingExpenses;
            // Update input data to reflect end of month status
            // account balances, mortgage, savings accounts etc
            this.updateMonthlyInputData(inputClone, monthlyResult, (currentInput) => {
                var _a;
                currentInput.mortgage.forEach((loan, idx) => {
                    /* eslint-disable no-param-reassign */
                    loan.monthlyAmortization = monthlyAmortization[idx];
                    loan.amount = Math.max(loan.amount - loan.monthlyAmortization, 0);
                });
                if ((_a = inputClone.additionalHousing) === null || _a === void 0 ? void 0 : _a.mortgage) {
                    for (const loan of inputClone.additionalHousing.mortgage) {
                        loan.amount = Math.max(loan.amount - loan.monthlyAmortization, 0);
                    }
                }
            });
            // Update input data to reflect end of year status
            // adult income, child age etc
            if (month % MONTHS_IN_YEAR === 11) {
                this.updateYearlyInputData(inputClone, monthlyResult);
            }
        }
        const resultsAfterInterestDeduction = calculateForecastResultAfterInterestDeduction(resultsBeforeInterestDeduction, input.adults.length, this.localizationParameters.mortgage);
        const bankDiscreationaryIncome = calculateBankDiscreationaryIncome(resultsAfterInterestDeduction, input.calculationInterestRate, input.adults.length, input.privateLoans, this.localizationParameters.mortgage);
        return {
            forecast: resultsAfterInterestDeduction,
            bankDiscreationaryIncome,
        };
    }
    /**
     * @returns Default localization parameters for swedish market
     */
    static getDefaultLocalizationParameters() {
        return deepClone(defaultForecastLocalizationParameters);
    }
}
